import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  OnDestroy,
  Output,
  ViewChild
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ControlValueAccessor,
  FormsModule,
  NG_VALUE_ACCESSOR
} from '@angular/forms';
import { IonicModule } from '@ionic/angular';
import { TranslateModule } from '@ngx-translate/core';
import { Subject, takeUntil } from 'rxjs';
import { Capacitor } from '@capacitor/core';

import {
  MentionState,
  RichtextEditorService
} from '../../services/richtext-editor.service';
import { MentionDropdownComponent } from '../mention-dropdown/mention-dropdown.component';
import { MentionPayload } from '../../nodes/mention.node';

@Component({
  selector: 'bl-richtext-editor',
  standalone: true,
  imports: [
    CommonModule,
    IonicModule,
    FormsModule,
    TranslateModule,
    MentionDropdownComponent
  ],
  template: `
    <div class="richtext-editor-container">
      <div
        class="editor-content"
        [attr.contenteditable]="!disabled"
        #contenteditable
        (click)="markTouched()"
        (keyup)="markTouched()"
        (keydown)="onKeyDown($event)"
        (focus)="onFocus()"
        (blur)="onBlur()"
        [attr.data-placeholder]="placeholder"
        [class.disabled]="disabled"
        [class.edit-mode]="editMode"
        [style.max-height]="maxHeight"
      ></div>

      <bl-mention-dropdown
        *ngIf="mentionsEnabled && mentionState.menuState !== 'closed'"
        [anchorRect]="mentionState.menuState.anchorRect"
        [users]="mentionState.users"
        [loading]="mentionState.loading"
        (userSelected)="onMentionSelect($event)"
      ></bl-mention-dropdown>

      <div
        *ngIf="mentionsEnabled"
        #mentionHintPopover
        popover="manual"
        class="mention-hint-popover"
      >
        {{ 'COMMON.INPUTS.MENTION_HINT' | translate }}
      </div>
    </div>
  `,
  styleUrls: ['./richtext-editor.component.scss'],
  providers: [
    RichtextEditorService,
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => RichtextEditorComponent),
      multi: true
    }
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RichtextEditorComponent
  implements AfterViewInit, OnDestroy, ControlValueAccessor
{
  @ViewChild('contenteditable') contenteditable?: ElementRef<HTMLDivElement>;
  @ViewChild('mentionHintPopover') mentionHintPopover?: ElementRef<HTMLElement>;

  @Input() placeholder = '';
  @Input() maxHeight?: string;
  @Input() mentionsEnabled = false;
  @Input() disabled = false;
  @Input() editMode = false;

  @Output() enterPressed = new EventEmitter<void>();

  mentionState: MentionState = {
    menuState: 'closed',
    users: [],
    loading: false
  };

  private destroy$ = new Subject<void>();
  private onChange: (value: string) => void = Function.prototype as () => void;
  private onTouched: () => void = Function.prototype as () => void;
  private currentValue = '';

  constructor(
    private editorService: RichtextEditorService,
    private cdr: ChangeDetectorRef
  ) {}

  writeValue(value: string): void {
    this.editorService.setValue(value || '');
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.cdr.markForCheck();
  }

  ngAfterViewInit(): void {
    if (!this.contenteditable) return;

    this.editorService.initializeEditor(this.contenteditable.nativeElement);
    this.setupMentions();
    this.setupValueSync();
  }

  ngOnDestroy(): void {
    this.editorService.destroyEditor();
    this.destroy$.next();
    this.destroy$.complete();
  }

  markTouched(): void {
    this.onTouched();
  }

  onFocus(): void {
    if (!this.currentValue)
      this.mentionHintPopover?.nativeElement?.showPopover?.();
  }

  onBlur(): void {
    this.mentionHintPopover?.nativeElement?.hidePopover?.();
    this.markTouched();
  }

  onKeyDown(event: KeyboardEvent): void {
    this.mentionHintPopover?.nativeElement?.hidePopover?.();

    if (this.mentionState.menuState !== 'closed') return;

    if (
      event.key === 'Enter' &&
      !event.shiftKey &&
      Capacitor.getPlatform() === 'web'
    ) {
      event.preventDefault();
      this.enterPressed.emit();
    }
  }

  onMentionSelect(payload: MentionPayload): void {
    this.editorService.insertMention(payload);
  }

  private setupMentions(): void {
    if (!this.mentionsEnabled) return;

    this.editorService.enableMentions();

    this.editorService.mentionState$
      .pipe(takeUntil(this.destroy$))
      .subscribe(state => {
        this.mentionState = state;
        this.cdr.markForCheck();
      });
  }

  private setupValueSync(): void {
    this.editorService.value$
      .pipe(takeUntil(this.destroy$))
      .subscribe(value => {
        this.currentValue = value;
        this.onChange(value);
      });
  }
}
